import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  fromEvent,
  merge,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ToastService } from './toast.service';
import { ToastType } from '../enums/config.enum';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class AutoLogoutService {
  private idleTimeout = 30 * 60 * 1000;
  private idleStop$ = new Subject<void>();
  isUserIdle = signal(false);
  private timerSubscription: Subscription | null = null;
  private logoutInProgress = false;
  toastService = inject(ToastService);

  constructor(
    private router: Router,
    private authService: AuthService
  ) {}

  setupAutoLogout(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    this.stopIdleWatch();
    this.idleStop$ = new Subject<void>();
    this.isUserIdle.set(false);
    this.logoutInProgress = false;

    const activityEvents$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'click'),
      fromEvent(document, 'scroll')
    );

    this.timerSubscription = activityEvents$
      .pipe(
        startWith(null),
        switchMap(() => timer(this.idleTimeout)),
        takeUntil(this.idleStop$)
      )
      .subscribe(() => {
        this.isUserIdle.set(true);
        this.logoutUser();
      });
  }

  logoutUser(): void {
    if (this.logoutInProgress || !this.timerSubscription) {
      return;
    }

    const userId =
      this.authService?.getUserDetailsFromStorage()?.userDetails?.userId;
    const token = this.authService.getToken();
    if (userId && token) {
      this.logoutInProgress = true;
      this.onLogoutHandler(token, userId, false);
    }
  }

  onLogoutHandler(
    token: string,
    userId: number,
    isExplicitLogout = false
  ): void {
    this.authService.logout(token, userId, isExplicitLogout).subscribe({
      next: (response) => {
        if (response) {
          this.clearTimerOnLogout();
          this.authService.cleanUpStorageData().subscribe({
            next: () => {
              this.authService.setIsLogoutClicked(true);
              this.toastService.addNotification({
                message: 'You have successfully logged out!',
                type: ToastType.Success,
              });
              this.router.navigate(['/login'], { replaceUrl: true });
            },
          });
        }
      },
      error: (_error: HttpErrorResponse) => {
        this.authService.setLoading(false);
        this.authService.cleanUpStorageData().subscribe({
          next: () => {
            this.authService.setIsLogoutClicked(true);
            this.router.navigate(['/login'], { replaceUrl: true });
          },
        });
      },
    });
  }

  clearTimerOnLogout(): void {
    this.stopIdleWatch();
  }

  private stopIdleWatch(): void {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = null;
    if (!this.idleStop$.closed) {
      this.idleStop$.next();
      this.idleStop$.complete();
    }
  }
}
